import { ApiError } from '@classgo/app-kit';
import { useCallback, useState } from 'react';
import type { z } from 'zod';

import { localizeIssue, localizeZodIssue } from './issues';
import type { FormErrors } from './types';

const NO_ERRORS: FormErrors = { fields: {}, form: null };

/**
 * A small form controller: values, per-field errors in Spanish, and a submit that validates
 * with the shared Zod schema first (the same rules the API applies) and then maps whatever the
 * API refuses back to the fields.
 *
 * @param {object} options -- The schema, the initial values and how values become the input.
 * @returns Values, setters, errors and `submit`.
 */
export const useForm = <V extends object, S extends z.ZodType>(options: {
  schema: S;
  initial: V;
  toInput: (values: V) => unknown;
}) => {
  const [values, setValues] = useState<V>(options.initial);
  const [errors, setErrors] = useState<FormErrors>(NO_ERRORS);
  const [busy, setBusy] = useState(false);

  const set = useCallback(<K extends keyof V>(key: K, value: V[K]) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) =>
      current.fields[key as string]
        ? { ...current, fields: { ...current.fields, [key as string]: '' } }
        : current,
    );
  }, []);

  const submit = async (action: (input: z.output<S>) => Promise<void>): Promise<boolean> => {
    const parsed = options.schema.safeParse(options.toInput(values));
    if (!parsed.success) {
      const next: FormErrors = { fields: {}, form: null };
      for (const issue of parsed.error.issues) {
        const localized = localizeZodIssue(issue);
        if (localized.field) next.fields[localized.field] ??= localized.message;
        else next.form ??= localized.message;
      }
      setErrors(next);
      return false;
    }
    setBusy(true);
    setErrors(NO_ERRORS);
    try {
      await action(parsed.data as z.output<S>);
      return true;
    } catch (thrown) {
      const next: FormErrors = { fields: {}, form: null };
      if (thrown instanceof ApiError) {
        const issues =
          thrown.issues.length > 0
            ? thrown.issues.map((issue) => localizeIssue(issue.message, issue.path))
            : [localizeIssue(thrown.message)];
        for (const issue of issues) {
          if (issue.field) next.fields[issue.field] ??= issue.message;
          else next.form ??= issue.message;
        }
        if (thrown.code === 'NETWORK')
          next.form = 'Sin conexión. Revise su red e intente de nuevo.';
      } else {
        next.form = 'No se pudo completar. Intente de nuevo.';
      }
      setErrors(next);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const fieldError = (key: keyof V & string): string | undefined => errors.fields[key] || undefined;

  return { values, set, errors, fieldError, busy, submit, reset: () => setValues(options.initial) };
};
